/* eslint-disable react/no-array-index-key */
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';

import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import TenderOfferBuyShares from 'modules/_shared/components/Modals/TenderOfferBuyShares';
import TenderOfferSellShares from 'modules/_shared/components/Modals/TenderOfferSellShares';
import { getPartnerCategories } from 'modules/partners/redux/partnerCategoryActions';

import {
  getDecimalScale,
  getPartnerFDPercent,
  getPartnerNDPercent,
  getPartnerPotentialFD,
} from 'utils/filters';
import { societyHasTitles } from 'utils/societyHasTitles';
import { tenderOfferSharesStatus } from 'utils/tenderOfferSharesTypes';
import isSuperAdmin from 'utils/isSuperAdmin';
import { hasShowDetails, isPlatformAXA } from 'utils/showDetails';
import { SocietyEquityMarketStatus } from 'types/Society';
import { isDevelopmentEnv, isLocalEnv } from 'constants/domains';

const getCurrentDecimalScale = (partner, society) => {
  if (society?.configuration?.decimalScale) {
    return society.configuration.decimalScale;
  }

  return partner?.societyValue?.value
    ? getDecimalScale(partner.societyValue.value)
    : 8;
};

export const usePartnerSociety = ({
  partner,
  society,
  shares,
  sharesTitles,
  coOwnerShares,
  societySharesTitleDocument,
  isReadOnly,
  refreshPartnerShares,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useSelector((state) => state?.user);
  const plans = useSelector((state) => state?.plans);
  const isAdmin = useSelector(
    (state) => state?.society?.role?.isAdmin || false
  );
  const partnerCategories = useSelector((state) => state?.partnerCategories);
  const tenderOffersShares = useSelector((state) => state?.tenderOffersShares);
  const partnerRepresentants = useSelector(
    (state) => state?.partnerRepresentants
  );
  const isReadOnlyUser = useSelector((state) =>
    partner?.readOnlyUsers?.includes(state.user['_id'])
  );

  const [hasEquityMarketEnabled, setHasEquityMarketEnabled] = useState(false);
  const [hasSellSharesEnabled, setHasSellSharesEnabled] = useState(false);
  const [hasBuySharesEnabled, setHasBuySharesEnabled] = useState(false);
  const [categories, setCategories] = useState([]);

  const [percents, setPercents] = useState({
    NDpercent: 0,
    FDpercent: 0,
    PFDPercent: 0,
  });

  useEffect(() => {
    if (!user) return;

    const isSocietyEquityMarketEnabled =
      !!society?.configuration?.equityMarket?.enabled;

    const isMarketUnavailable =
      society?.configuration?.equityMarket?.status ===
      SocietyEquityMarketStatus.UNAVAILABLE;

    const isPartnerAllowed = (partner?.sharesCount?.actual || 0) > 0;

    const isPartnerEquityMarketAllowed =
      !!partner?.configuration?.equityMarket?.enabled;

    const isUserSuperAdmin = isSuperAdmin(user);

    if (hasShowDetails()) {
      // eslint-disable-next-line no-console
      console.log('🚀 cclog ~ usePartnerSociety:', {
        isAdmin,
        isUserSuperAdmin,
        isPartnerAllowed,
        isPartnerEquityMarketAllowed,
        isSocietyEquityMarketEnabled,
        isMarketUnavailable,
        isDevelopmentEnv,
      });
    }

    if (isMarketUnavailable || !isSocietyEquityMarketEnabled) {
      setHasEquityMarketEnabled(false);
      return;
    }

    const isVisibleForAXA = Boolean(
      isPlatformAXA && (isPartnerEquityMarketAllowed || isAdmin)
    );

    const isVisibleForSttok = Boolean(
      (!isPlatformAXA && isPartnerEquityMarketAllowed && isPartnerAllowed) ||
        isUserSuperAdmin
    );

    setHasEquityMarketEnabled(
      isPlatformAXA ? isVisibleForAXA : isVisibleForSttok
    );
  }, [user, partner, society, isAdmin]);

  const decimalScale = useMemo(
    () => getCurrentDecimalScale(partner, society),
    [partner, society]
  );

  const hasMainSharesTitleDisabled = useMemo(
    () =>
      (partner?.sharesCount?.actual || 0) ===
      (partner?.sharesCount?.coOwned || 0),
    [partner]
  );

  const handleOpenBuyShares = () => {
    if (isReadOnly || isReadOnlyUser) return;
    dispatch(
      setModal(<TenderOfferBuyShares partner={partner} size={sizes.LG} />)
    );
  };

  const handleOpenSellShares = () => {
    if (isReadOnly || isReadOnlyUser) return;
    dispatch(
      setModal(<TenderOfferSellShares partner={partner} size={sizes.LG} />)
    );
  };

  const handleOpenEquityMarket = () => {
    if (isReadOnly || isReadOnlyUser) return;
    const queryParam = partner?._id ? `?partnerId=${partner._id}` : '';
    history.push(`/mercado-de-acciones/${society?._id}${queryParam}`);
  };

  const calculatePercents = useCallback(() => {
    setPercents({
      NDpercent: Number(
        getPartnerNDPercent(partner, society).toFixed(decimalScale)
      ),
      FDpercent: Number(
        getPartnerFDPercent(partner, society).toFixed(decimalScale)
      ),
      PFDPercent: Number(
        getPartnerPotentialFD(partner, society, plans).toFixed(decimalScale)
      ),
    });
  }, [partner, society, plans, decimalScale]);

  useEffect(() => {
    if (!partner || !society) return;

    calculatePercents();
  }, [partner, society, calculatePercents]);

  useEffect(() => {
    if (!tenderOffersShares?.length) return;

    const hasTenderOffersSharesForSell = tenderOffersShares?.some(
      (tenderOfferShare) =>
        tenderOfferShare?.type === 'BUY' &&
        tenderOfferShare?.status === tenderOfferSharesStatus.ACTIVE
    );

    const hasTenderOffersSharesForBuy = tenderOffersShares?.some(
      (tenderOfferShare) =>
        tenderOfferShare?.type === 'SELL' &&
        tenderOfferShare?.status === tenderOfferSharesStatus.ACTIVE
    );

    setHasSellSharesEnabled(hasTenderOffersSharesForSell);
    setHasBuySharesEnabled(hasTenderOffersSharesForBuy);
  }, [tenderOffersShares]);

  useEffect(() => {
    const hasSingleCategory = partner?.category;
    const hasMultipleCategories = partner?.categories?.length;

    if (partnerCategories?.length) {
      if (!hasMultipleCategories && hasSingleCategory) {
        const category = partnerCategories.find(
          (category) => category['_id'] === partner.category
        );
        setCategories([category]);
      } else if (hasMultipleCategories) {
        const categories = partnerCategories.filter((category) =>
          partner.categories.includes(category['_id'])
        );
        setCategories(categories);
      }
    }

    const hasCategoryInfo = hasSingleCategory || hasMultipleCategories;
    const noPartnerCategories = !partnerCategories?.length;

    const categoriesCondition =
      hasCategoryInfo && noPartnerCategories && !society?._id;

    if (categoriesCondition) {
      dispatch(getPartnerCategories(society?._id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerCategories]);

  return {
    data: {
      fullName: partner?.name || '',
      categories,
      partnerRepresentants,
      hasSellSharesEnabled,
      hasBuySharesEnabled,
      hasSharesTitlesEnabled: societyHasTitles(society),
      hasEquityMarketEnabled,
      hasMainSharesTitleDisabled,
      isReadOnlyUser,
      decimalScale,
      NDpercent: percents.NDpercent,
      FDpercent: percents.FDpercent,
      PFDPercent: percents.PFDPercent,
    },
    actions: {
      handleOpenBuyShares,
      handleOpenSellShares,
      handleOpenEquityMarket,
    },
  };
};
